import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Grid, InputAdornment, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FormikInputString, P2, Modal, FormikInputPhone, FormikInputZip } from 'src/components';
import { selectTenantCreatingErrors } from 'src/redux/tenants/selectors';
import { CreateTenantPayload, useCreateTenant } from 'src/api/tenant';
import { tenantScheme } from 'src/utils/validationSchemes';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreateSuccess: (id: number) => void;
};

const AddCompanyDialog: React.FC<Props> = ({ open, onClose, onCreateSuccess }): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const tenantCreatingErrors = useSelector(selectTenantCreatingErrors) as Record<string, string[]>;

  const formik = useFormik({
    initialValues: {
      title: '',
      address1_country: '',
      address1_city: '',
      address1_street: '',
      address1_state: '',
      address1_zip: '',
      address2_country: '',
      address2_city: '',
      address2_street: '',
      address2_state: '',
      address2_zip: '',
      phone: '',
      website: '',
      contact_name: '',
      contact_phone: '',
      slug: '',
    },
    validationSchema: tenantScheme,
    onSubmit: async (data) => {
      let rawWebsiteData = data.website || '';
      rawWebsiteData = rawWebsiteData.replace(/^(https?:\/\/)/i, '');

      const newCompany: CreateTenantPayload = {
        title: data.title,
        address1: {
          country: data.address1_country,
          city: data.address1_city,
          street: data.address1_street,
          state: data.address1_state,
          zip: data.address1_zip,
        },
        address2: {
          country: data.address2_country,
          city: data.address2_city,
          street: data.address2_street,
          state: data.address2_state,
          zip: data.address2_zip,
        },
        phone: data.phone,
        website: rawWebsiteData ? `https://${rawWebsiteData}` : null,
        pointOfContact: {
          name: data.contact_name,
          number: data.contact_phone ? data.contact_phone : null,
        },
        // slug: data.slug,
      };

      mutateCreateTenant(newCompany);
    },
  });

  const { mutate: mutateCreateTenant, isPending: isTenantCreating } = useCreateTenant({
    meta: {
      setError: formik.setErrors,
    },
    onSuccess: (response) => {
      onClose();
      formik.resetForm();
      onCreateSuccess(response.payload);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal
      open={open}
      fullScreen={fullScreen}
      onClose={handleClose}
      onOk={() => formik.handleSubmit()}
      loading={isTenantCreating}
      title={t('companyDashboardPage.addCompany')}
    >
      <Grid container rowSpacing={0} columnSpacing={3}>
        <Grid item xs={12}>
          <FormikInputString
            formik={formik}
            field="title"
            label={t('companyDashboardPage.companyName')}
            autoFocus
            errorHelper={tenantCreatingErrors}
          />
        </Grid>

        <Grid item xs={12}>
          <FormikInputPhone formik={formik} field="phone" label={t('companyDashboardPage.phone')} />
        </Grid>
        <Grid item xs={12}>
          <FormikInputString
            formik={formik}
            field="website"
            label={t('companyDashboardPage.website')}
            InputProps={{ startAdornment: <InputAdornment position="start">https://</InputAdornment> }}
          />
        </Grid>

        <Grid item xs={12} sx={{ mb: 1 }}>
          <P2 sx={{ fontWeight: 600 }}>{t('companyDashboardPage.address1')}</P2>
        </Grid>
        <Grid item xs={6}>
          <FormikInputString formik={formik} field="address1_country" label={t('companyDashboardPage.country')} />
        </Grid>
        <Grid item xs={6}>
          <FormikInputString formik={formik} field="address1_city" label={t('companyDashboardPage.city')} />
        </Grid>
        <Grid item xs={12}>
          <FormikInputString formik={formik} field="address1_street" label={t('companyDashboardPage.street')} />
        </Grid>
        <Grid item xs={6}>
          <FormikInputString formik={formik} field="address1_state" label={t('companyDashboardPage.state')} />
        </Grid>
        <Grid item xs={6}>
          <FormikInputZip formik={formik} field="address1_zip" label={t('companyDashboardPage.zip')} />
        </Grid>

        {/* // ERMA-3239: this functionality is not needed for now */}
        {/* <Grid item xs={12} sx={{ mb: 2 }}>
          <P2 sx={{ fontWeight: 600 }}>{t('companyDashboardPage.companyUrl')}</P2>
          <P3 component="p" sx={{ color: themeColors.grayDark, lineHeight: 1.4 }}>
            {t('companyDashboardPage.companyUrlDesc')}
          </P3>
        </Grid>
        <Grid item xs={12}>
          <FormikInputString
            formik={formik}
            label={t('companyDashboardPage.companyUrl')}
            placeholder="mywebsite"
            field="slug"
            errorHelper={tenantCreatingErrors}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ marginRight: 0 }}>
                  <P1R sx={{ color: themeColors.black }}>https://</P1R>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end" sx={{ marginLeft: 0 }}>
                  <P1R sx={{ color: themeColors.black }}>{`.${config.DOMAIN}`}</P1R>
                </InputAdornment>
              ),
            }}
          />
        </Grid> */}
      </Grid>
    </Modal>
  );
};

export default AddCompanyDialog;
